import axios from 'axios';
import { customerSignIn } from './leadService';
import useChatStore from '../store/useChatStore';
import showToast from '../utility/showToast';

let isRefreshing = false; // Prevent multiple simultaneous refresh calls
let failedQueue = []; // Queue for requests that failed during token refresh
let lastActivityTime = Date.now(); // Track last user activity
const INACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Process all queued requests after token refresh
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Update last activity time
 */
const updateActivity = () => {
  lastActivityTime = Date.now();
};

/**
 * Check if user has been inactive for too long
 * @returns {boolean} true if inactive beyond threshold
 */
const isUserInactive = () => {
  const inactiveDuration = Date.now() - lastActivityTime;
  return inactiveDuration > INACTIVITY_THRESHOLD;
};

/**
 * Setup activity tracking listeners
 */
const setupActivityTracking = () => {
  // Track clicks
  document.addEventListener('click', updateActivity, { passive: true });
  
  // Track scrolls (with passive flag for better performance)
  document.addEventListener('scroll', updateActivity, { passive: true });
  
  // Track keyboard events
  document.addEventListener('keypress', updateActivity, { passive: true });
  
  // Track mouse movement (throttled to avoid performance issues)
  let mouseMoveTimeout;
  document.addEventListener('mousemove', () => {
    if (!mouseMoveTimeout) {
      updateActivity();
      mouseMoveTimeout = setTimeout(() => {
        mouseMoveTimeout = null;
      }, 5000); // Update at most once every 5 seconds
    }
  }, { passive: true });

  console.log('[Activity Tracker] User activity tracking enabled');
};

/**
 * Setup Axios Response Interceptor for automatic token refresh
 * Call this once when app starts (in App.js or index.js)
 */
export const setupAxiosInterceptor = () => {
  // Setup user activity tracking
  setupActivityTracking();
  
  axios.interceptors.response.use(
    // Success response - update activity and pass through
    (response) => {
      updateActivity(); // API calls are also user activity
      return response;
    },
    
    // Error response - check if token expired
    async (error) => {
      const originalRequest = error.config;

      // Check if error is 401 (Unauthorized) and request hasn't been retried yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        
        // If token is already being refreshed, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['authorization'] = token;
              return axios(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          console.log('[Interceptor] Token expired (401), checking user activity...');
          
          // Check if user has been inactive for too long
          if (isUserInactive()) {
            const inactiveMinutes = Math.floor((Date.now() - lastActivityTime) / 60000);
            console.log(`[Interceptor] User inactive for ${inactiveMinutes} minutes - forcing logout`);
            throw new Error(`Session expired due to inactivity (${inactiveMinutes} minutes). Please login again.`);
          }
          
          console.log('[Interceptor] User recently active - attempting token refresh...');
          
          // Get customer mobile from Zustand store
          const customer = useChatStore.getState().customer;
          const mobile = customer?.mobile || customer?.mobileNumber || customer?.phoneNumber;

          if (!mobile) {
            console.error('[Interceptor] No mobile number found in customer data');
            throw new Error('Unable to refresh token: No mobile number found');
          }

          // Call sign-in API to get new token
          const signInResult = await customerSignIn(mobile);

          if (signInResult.success && signInResult.token) {
            console.log('[Interceptor] Token refreshed successfully');
            
            // Update token in Zustand store
            useChatStore.getState().setAuthToken(signInResult.token);
            
            // Update authorization header for this request
            originalRequest.headers['authorization'] = signInResult.token;
            
            // Update default axios headers for future requests
            axios.defaults.headers.common['authorization'] = signInResult.token;
            
            // Update activity time since token refresh succeeded
            updateActivity();
            
            // Process all queued requests with new token
            processQueue(null, signInResult.token);
            
            // Retry the original failed request with new token
            return axios(originalRequest);
          } else if (signInResult.isNewCustomer) {
            // This shouldn't happen (user was logged in but now no leads found)
            console.error('[Interceptor] Customer no longer has leads');
            throw new Error('Session invalid. Please login again.');
          } else {
            throw new Error(signInResult.message || 'Token refresh failed');
          }
        } catch (err) {
          console.error('[Interceptor] Token refresh failed:', err);
          
          // Process queued requests with error
          processQueue(err, null);
          
          // Logout user and redirect to home
          showToast('Your session has expired. Please login again.', 'error');
          useChatStore.getState().logout();
          
          // Redirect after a short delay
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
          
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      // For non-401 errors, just pass through
      return Promise.reject(error);
    }
  );

  console.log('[Interceptor] Axios interceptor setup complete');
};

